<template>
  <div class="bg-white rounded-lg shadow-lg overflow-hidden h-full flex flex-col">
    <!-- Header -->
    <div class="bg-blue-600 text-white p-6">
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-2xl font-bold">{{ roomDisplayLabel }}</h3>
        <button
          @click="$emit('close')"
          class="text-white hover:text-gray-200 transition"
          :aria-label="$t('admin.close')"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div class="flex items-center gap-4 text-sm">
        <span v-if="room.bedrooms" class="flex items-center gap-1">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          {{ room.bedrooms }} {{ room.bedrooms > 1 ? $t('property.bedrooms') : $t('property.bedroom') }}
        </span>
      </div>
    </div>
    
    <!-- Content -->
    <div class="flex-1 overflow-y-auto">
      <!-- Description -->
      <div v-if="roomDescription" class="p-6 border-b">
        <p class="text-gray-700 leading-relaxed whitespace-pre-line">{{ roomDescription }}</p>
      </div>

      <div v-else-if="showPolygonOnlyHint" class="p-6 border-b">
        <p class="text-gray-600 leading-relaxed">{{ $t('floorPlan.polygonOnlyHint') }}</p>
      </div>
      
      <!-- Image Gallery -->
      <div v-if="room.images && room.images.length > 0" class="p-6">
        <h4 class="text-lg font-semibold mb-4 text-gray-800">{{ $t('property.gallery') }}</h4>
        <div class="swiper-container">
          <swiper
            :modules="modules"
            :slides-per-view="1"
            :space-between="10"
            :pagination="{ clickable: true }"
            :navigation="true"
            class="rounded-lg overflow-hidden"
          >
            <swiper-slide v-for="(image, index) in room.images" :key="index">
              <div class="relative h-64 rounded-lg overflow-hidden">
                <img
                  :src="resolvePropertyImageUrl(image.src)"
                  :alt="image.alt || roomDisplayLabel"
                  class="w-full h-full object-cover"
                />
                <div
                  v-if="image.caption"
                  class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4"
                >
                  <p class="text-white text-sm whitespace-pre-line">{{ image.caption }}</p>
                </div>
              </div>
            </swiper-slide>
          </swiper>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { resolvePropertyImageUrl } from '../../utils/propertyImageUrl';
import { normalizeRoomCoordinatesForViewer } from '@merlot/common/floorPlanCoordinates.js';

const modules = [Navigation, Pagination];

const props = defineProps({
  room: {
    type: Object,
    required: true
  }
});

const roomDisplayLabel = computed(() => {
  const label = (props.room?.label || '').trim();
  if (label) return label;
  return (props.room?.name || '').trim();
});

const roomDescription = computed(() => (props.room?.description || '').trim());

const hasPhotos = computed(
  () => Array.isArray(props.room?.images) && props.room.images.length > 0
);

const hasPlanPolygon = computed(() => {
  const norm = normalizeRoomCoordinatesForViewer(props.room?.coordinates);
  if (norm?.points) return true;
  const c = props.room?.coordinates;
  return !!(c && c.x != null && c.y != null && c.width != null && c.height != null);
});

const showPolygonOnlyHint = computed(
  () => hasPlanPolygon.value && !roomDescription.value && !hasPhotos.value
);

defineEmits(['close']);
</script>

<style scoped>
.swiper-button-next,
.swiper-button-prev {
  color: white;
  background: rgba(0, 0, 0, 0.5);
  width: 36px;
  height: 36px;
  border-radius: 50%;
}

.swiper-button-next:after,
.swiper-button-prev:after {
  font-size: 16px;
}

.swiper-pagination-bullet-active {
  background: white;
}
</style>

